import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/browser";
import { V2Alert, V2Button, V2TextField } from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-relay/hooks";
import { ConnectionHandler } from "relay-runtime";

import {
  NON_SSO_ROLE,
  EDIT_FORM_SUBMIT_BUTTON_NAME,
  EDIT_FORM_LOADING_SUBMIT_BUTTON_NAME,
} from "app/constants";
import { useFormErrors } from "app/hooks";
import { AddUserMutation, UpdateUserMutation } from "app/mutations";

import { schema } from "./schema";

import type { FilterElementInput } from "../__generated__/UserListRefetchQuery.graphql";
import type {
  AddUserInput,
  AddUserMutation as AddUserMutationType,
} from "app/mutations/__generated__/AddUserMutation.graphql";
import type {
  UpdateUserInput,
  UpdateUserMutation as UpdateUserMutationType,
} from "app/mutations/__generated__/UpdateUserMutation.graphql";

interface Props {
  onAdded: (message: string) => void;
  onCancel: () => void;
  filterElement?: FilterElementInput;
  selectedEditUserData?: UpdateUserInput;
}

const defaultUserData = {
  name: "",
  email: "",
  key: 0,
};

export function UpsertUserForm({
  selectedEditUserData = defaultUserData,
  onAdded,
  onCancel,
  filterElement,
}: Props) {
  const [formErrors, setFormErrors] = useFormErrors();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<UpdateUserInput & AddUserInput>({
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const [addUser, isInFlightAdd] =
    useMutation<AddUserMutationType>(AddUserMutation);

  const addUserAction = (data: AddUserInput) => {
    const connectionId = ConnectionHandler.getConnectionID(
      "client:root",
      "UserList_users",
      {
        filterElement: filterElement ?? null,
      },
    );

    const { name, email } = data;
    const formattedUsername = email.replace("@", "_at_");

    addUser({
      variables: {
        input: {
          name,
          username: formattedUsername,
          email,
          roles: [NON_SSO_ROLE],
        },
        connections: [connectionId],
      },
      onCompleted(response, errors) {
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
        }
        if (response.addUser?.user) {
          onAdded(`Added user ${response.addUser.user.name} successfully`);
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
    });
  };

  const updateUserAction = (data: UpdateUserInput) => {
    const { name, email } = data;

    updateUser({
      variables: {
        input: {
          key: selectedEditUserData.key,
          name,
          email,
        },
      },
      onCompleted(response, errors) {
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
        }
        if (response.updateUser?.user) {
          onAdded(`Edited user ${response.updateUser.user.name} successfully`);
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
    });
  };

  const [updateUser, isInFlightUpdate] =
    useMutation<UpdateUserMutationType>(UpdateUserMutation);

  const isInFlight = selectedEditUserData.name
    ? isInFlightUpdate
    : isInFlightAdd;

  return (
    <div>
      <form
        className="mt-2 min-w-[500px] text-legacy-navy-L30"
        onSubmit={
          selectedEditUserData.name
            ? handleSubmit(updateUserAction)
            : handleSubmit(addUserAction)
        }
      >
        {!selectedEditUserData.name ? (
          <p className="mb-lg">
            The new admin will receive an email with a temporary password to
            activate their account.
          </p>
        ) : null}
        <Controller
          control={control}
          defaultValue={selectedEditUserData.name ?? ""}
          name="name"
          render={({ field, fieldState }) => (
            <V2TextField
              className="w-full"
              inputProps={{
                placeholder: "Enter the name for this admin",
              }}
              isInvalid={fieldState.invalid}
              label="Name *"
              isRequired
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          defaultValue={selectedEditUserData.email ?? ""}
          name="email"
          render={({ field, fieldState }) => (
            <V2TextField
              className="mt-xl w-full"
              inputProps={{
                placeholder: "Enter the email address for this admin",
              }}
              isInvalid={fieldState.invalid}
              label="Email address *"
              isRequired
              {...field}
            />
          )}
        />

        {formErrors
          ? formErrors.map((error) => (
              <div className="mt-xl" key={error.id}>
                <V2Alert severity="error">{error.message}</V2Alert>
              </div>
            ))
          : null}
        <div className="mb-lg mt-xl flex justify-between">
          <V2Button onPress={onCancel} variant="secondary">
            Cancel
          </V2Button>
          <V2Button
            data-testid="create-user-button"
            isDisabled={!isValid || isInFlight}
            isPending={isInFlight}
            pendingText={
              selectedEditUserData.name
                ? EDIT_FORM_LOADING_SUBMIT_BUTTON_NAME
                : "Creating and sending invite…"
            }
            type="submit"
            variant="primary"
          >
            {selectedEditUserData.name
              ? EDIT_FORM_SUBMIT_BUTTON_NAME
              : "Create and send invite"}
          </V2Button>
        </div>
      </form>
    </div>
  );
}
